import axios from 'axios'
import { ElMessage } from 'element-plus'
import { getToken, removeToken } from '../utils/auth'
import { clearPermissionCache } from '../utils/auth'
import { API_BASE_URL } from '../constants/env'
import router from '../router'

const request = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000
})

// 请求拦截：自动加 token；无 token 时只允许登录接口
request.interceptors.request.use(
  (config) => {
    const token = getToken()
    if (token) {
      config.headers.Authorization = 'Bearer ' + token
    } else {
      // 无 token 时，只允许登录接口
      const url = config.url || ''
      if (!url.includes('/admin/auth/login')) {
        return Promise.reject(new Error('NO_TOKEN'))
      }
    }
    return config
  },
  (error) => Promise.reject(error)
)

// 响应拦截：解包 ApiResponse（Blob/ArrayBuffer 响应除外）
request.interceptors.response.use(
  (res) => {
    // 二进制响应直接返回完整 axios response（不解包）
    if (res.config.responseType === 'blob' || res.config.responseType === 'arraybuffer') {
      return res
    }
    const { code, message, data } = res.data
    if (code === 0) {
      return data
    }
    ElMessage.error(message || '请求失败')
    return Promise.reject(new Error(message))
  },
  (err) => {
    if (err.message === 'NO_TOKEN') {
      // 无 token 静默跳转登录页，不弹错误提示
      router.push('/login')
      return Promise.reject(err)
    }
    if (err.response && err.response.status === 401) {
      removeToken()
      // 修复：同步清除内存中的权限缓存，防止下一账号复用上一账号权限
      clearPermissionCache()
      router.push('/login')
      ElMessage.error('登录已过期，请重新登录')
    } else if (err.response && err.response.data && err.response.data.message) {
      ElMessage.error(err.response.data.message)
    } else {
      ElMessage.error(err.message || '网络错误')
    }
    return Promise.reject(err)
  }
)

// ==================== 认证相关 ====================

/** 登录 */
export const login = (data) => request.post('/admin/auth/login', data)

/** 登出 */
export const logout = () => request.post('/admin/auth/logout')

/** 获取当前用户信息 */
export const getUserInfo = () => request.get('/admin/auth/info')

/** 获取当前用户菜单树 */
export const getUserMenus = () => request.get('/admin/sys/menu/tree')

/** 获取系统菜单树（菜单管理用） */
export const getMenuTree = () => request.get('/admin/sys/menu/tree')

// ==================== 工作台统计 ====================

/** 今日调用量 / 成功率 / 今日收入 / 活跃商户 等概览数据 */
export const getDashboardStats = () => request.get('/admin/stats/dashboard')

// ==================== 商户管理 ====================

/** 商户分页列表 */

/** 商户详情 */
export const getMerchant = (id) => request.get(`/admin/merchant/${id}`)

/** 新增商户 */
export const createMerchant = (data) => request.post('/admin/merchant', data)

/** 修改商户 */
export const updateMerchant = (id, data) => request.put(`/admin/merchant/${id}`, data)

/** 删除商户 */
export const deleteMerchant = (id) => request.delete(`/admin/merchant/${id}`)

// ==================== API 服务管理 ====================

/** API 分页列表 */

/** API 详情 */
export const getApi = (id) => request.get(`/admin/api/${id}`)

/** 新增 API */
export const createApi = (data) => request.post('/admin/api', data)

/** 修改 API */
export const updateApi = (id, data) => request.put(`/admin/api/${id}`, data)

/** 删除 API */
export const deleteApi = (id) => request.delete(`/admin/api/${id}`)

// ==================== 调用日志 ====================

/** 调用日志分页查询 */

/** 操作日志分页查询 */

/** 登录日志分页查询 */

// ==================== 通用：Blob 下载 ====================

/** 从 Blob 响应触发浏览器下载 */
export function saveAs(response, fallbackName = 'download') {
  const blob = response.data instanceof Blob ? response.data : new Blob([response.data])
  const disposition = response.headers?.['content-disposition'] || ''
  let fileName = fallbackName
  const match = disposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i)
  if (match) fileName = decodeURIComponent(match[1].replace(/"/g, ''))
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = fileName
  a.click()
  URL.revokeObjectURL(url)
}

// 导出 request 实例供其他模块使用
export default request
